/* Formatting helpers — consistent numerals across the app. Serif tabular numerals
   are applied via the .num class in CSS; these produce the strings. */

export function money(n: number | undefined | null, opts: { compact?: boolean; cents?: boolean } = {}): string {
  if (n == null || Number.isNaN(n)) return '—'
  if (opts.compact) {
    const abs = Math.abs(n)
    if (abs >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(1)}B`
    if (abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(abs >= 10_000_000 ? 1 : 2)}M`
    if (abs >= 1_000) return `$${(n / 1_000).toFixed(0)}K`
  }
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: opts.cents ? 2 : 0,
    maximumFractionDigits: opts.cents ? 2 : 0,
  })
}

export function num(n: number | undefined | null, digits = 0): string {
  if (n == null || Number.isNaN(n)) return '—'
  return n.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits })
}

export function pct(n: number | undefined | null, digits = 1): string {
  if (n == null || Number.isNaN(n)) return '—'
  return `${n.toFixed(digits)}%`
}

/** Variance vs budget as a +/- string. */
export function deltaStr(n: number | undefined | null, kind: 'money' | 'pct' | 'num' = 'money'): string {
  if (n == null || Number.isNaN(n)) return '—'
  const sign = n > 0 ? '+' : ''
  if (kind === 'pct') return `${sign}${n.toFixed(1)}%`
  if (kind === 'num') return `${sign}${num(n)}`
  return `${sign}${money(n)}`
}

export type Tone = 'up' | 'down' | 'flat'
/** higherIsBetter=false for cost lines where over-budget is bad. */
export function tone(delta: number | undefined | null, higherIsBetter = true): Tone {
  if (delta == null || Math.abs(delta) < 1e-9) return 'flat'
  const positive = delta > 0
  const good = higherIsBetter ? positive : !positive
  return good ? 'up' : 'down'
}

export function date(d: string | undefined | null, style: 'short' | 'med' | 'long' = 'med'): string {
  if (!d) return '—'
  const dt = new Date(d.length === 10 ? `${d}T00:00:00` : d)
  if (Number.isNaN(dt.getTime())) return d
  const opts: Intl.DateTimeFormatOptions =
    style === 'short' ? { month: 'numeric', day: 'numeric', year: '2-digit' }
    : style === 'long' ? { month: 'long', day: 'numeric', year: 'numeric' }
    : { month: 'short', day: 'numeric', year: 'numeric' }
  return dt.toLocaleDateString('en-US', opts)
}

/** Local-timezone ISO date for "now" — replaces the prototype's frozen
 *  2026-06-13 demo clock. */
export function todayIso(): string {
  const n = new Date()
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`
}

export function daysUntil(d: string | undefined | null, today = todayIso()): number | null {
  if (!d) return null
  const a = new Date(`${d}T00:00:00`).getTime()
  const b = new Date(`${today}T00:00:00`).getTime()
  if (Number.isNaN(a) || Number.isNaN(b)) return null
  return Math.round((a - b) / 86_400_000)
}

export function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('')
}
